import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  tryGetActiveWorkspaceId,
  setActiveWorkspace,
} from "../services/workspace/activeWorkspace";

/** URL query-param key driving the workspace selection. */
export const WORKSPACE_PARAM = "workspace";

export interface UseSelectedWorkspaceResult {
  /** Currently selected workspace id from the URL, or `null` when unset. */
  selectedId: string | null;
  /**
   * Update the selected workspace id. Writes to both the URL param and
   * localStorage so the selection survives cross-page navigation.
   *
   * Passing `null` removes the param.
   */
  setSelectedId: (id: string | null) => void;
}

/**
 * Workspace selection hook — URL-driven with localStorage persistence.
 *
 * - Selection is stored in both `?workspace=<id>` (URL) and localStorage.
 * - On mount, if the URL has no workspace param (e.g. after page navigation),
 *   the hook restores the last selection from localStorage automatically.
 * - Bridges the legacy `services/workspace/activeWorkspace.ts` localStorage
 *   mechanism so KG and other features that read `getActiveWorkspaceId()` stay
 *   consistent with whatever the user selected in the dropdown.
 */
export const useSelectedWorkspace = (): UseSelectedWorkspaceResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedId = useMemo(
    () => searchParams.get(WORKSPACE_PARAM),
    [searchParams],
  );

  // Restore the last selection from localStorage when the URL loses the param
  // (happens on every cross-page navigation since params are not carried over).
  useEffect(() => {
    if (selectedId !== null) return;
    const stored = tryGetActiveWorkspaceId();
    if (!stored) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(WORKSPACE_PARAM, stored);
        return next;
      },
      { replace: true },
    );
  }, [selectedId, setSearchParams]);

  const setSelectedId = useCallback(
    (id: string | null) => {
      // Persist to localStorage so the selection survives page navigation.
      if (id !== null) {
        setActiveWorkspace({ workspaceId: id, tenantId: null });
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id === null) next.delete(WORKSPACE_PARAM);
          else next.set(WORKSPACE_PARAM, id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { selectedId, setSelectedId };
};
